import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme, Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ReactComponent as BrainIcon } from '../../../../assets/images/loading-white.svg';

import {
  ContentWrapper,
  TypedText,
  StyledForm,
  WaitingMessage,
} from '../HomeInputStyles';

// Componentes reutilizables
import InputField from '../components/InputField';
import CustomizeButton from '../components/CustomizeButton';

// Gradientes específicos para cada AGT (desde AGTPromo)
// Light mode (originales)
const CHAT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #415B7E, #2D476A, #193356)';
const UPS_GRADIENT_LIGHT = 'linear-gradient(to bottom, #DC9844, #C88430, #B4701C)';
const JOB_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4A4A4A, #333333, #1A1A1A)';
const FIT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #FF8C00, #FF6B00, #FF5722)';
const HEALTH_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4CAF50, #3d8b40, #2e7031)';

// Dark mode (versiones más oscuras - reducidos ~40%)
const CHAT_GRADIENT_DARK = 'linear-gradient(to bottom, #2A3D54, #1E2F45, #0F1A2B)';
const UPS_GRADIENT_DARK = 'linear-gradient(to bottom, #8A5F2A, #7A501D, #6B4312)';
const JOB_GRADIENT_DARK = 'linear-gradient(to bottom, #2D2D2D, #1F1F1F, #0F0F0F)';
const FIT_GRADIENT_DARK = 'linear-gradient(to bottom, #994F00, #994000, #993314)';
const HEALTH_GRADIENT_DARK = 'linear-gradient(to bottom, #2D6930, #245326, #1B421D)';


// Componente wrapper para BrainIcon con gradiente
// En light/dark mode: SVG con gradiente
// En wallpaper mode: Fit usa gradiente, las demás apps usan el color más oscuro sólido
const BrainIconWithGradient = ({ gradient, gradientDark, appId }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';
  const gradientId = `brain-gradient-${appId}`;

  if (!gradient) {
    // Sin gradiente definido: cerebro blanco
    return <BrainIcon />;
  }

  // Extraer colores del gradiente
  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  // Función para invertir color (negativo suavizado - gris en lugar de blanco)
  const invertColor = (hex) => {
    // Remover # si existe
    hex = hex.replace('#', '');

    // Convertir a RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Invertir cada componente pero con un máximo de 180 en lugar de 255
    // Esto produce grises suaves en lugar de blancos brillantes
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');

    return `#${invR}${invG}${invB}`;
  };

  // Seleccionar gradiente según modo
  const activeGradient = (isDarkMode && gradientDark) ? gradientDark : gradient;
  const colors = extractColors(activeGradient);

  // En wallpaper mode
  if (isWallpaperMode) {
    // Caso especial: Fit mantiene el gradiente en wallpaper mode
    if (appId === 'fit') {
      return (
        <>
          <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                {colors.map((color, index) => (
                  <stop
                    key={index}
                    offset={`${(index / (colors.length - 1)) * 100}%`}
                    stopColor={color}
                  />
                ))}
              </linearGradient>
            </defs>
          </svg>
          <Box
            component="span"
            sx={{
              display: 'contents',
              '& svg path': {
                fill: `url(#${gradientId})`,
                stroke: `url(#${gradientId})`,
              }
            }}
          >
            <BrainIcon />
          </Box>
        </>
      );
    }

    // Caso especial: Fin usa blanco en wallpaper para mejor visibilidad
    if (appId === 'fin') {
      return (
        <Box
          component="span"
          sx={{
            display: 'contents',
            '& svg path': {
              fill: '#FFFFFF',
              stroke: '#FFFFFF',
            }
          }}
        >
          <BrainIcon />
        </Box>
      );
    }

    // Para las demás apps en wallpaper: color sólido más oscuro
    const darkestColor = colors[colors.length - 1] || '#1A1A1A';

    return (
      <Box
        component="span"
        sx={{
          display: 'contents',
          '& svg path': {
            fill: darkestColor,
            stroke: darkestColor,
          }
        }}
      >
        <BrainIcon />
      </Box>
    );
  }

  // Caso especial: Chat en dark mode (no wallpaper) usa color sólido más claro
  if (appId === 'chat' && isDarkMode) {
    const lightestColor = colors[0] || '#415B7E';
    return (
      <Box
        component="span"
        sx={{
          display: 'contents',
          '& svg path': {
            fill: lightestColor,
            stroke: lightestColor,
          }
        }}
      >
        <BrainIcon />
      </Box>
    );
  }

  // Caso especial: Fin usa colores negativos en ambos modos para diferenciarse
  const shouldInvertColors = appId === 'fin';
  const finalColors = shouldInvertColors ? colors.map(invertColor) : colors;

  // En light/dark mode: SVG con gradiente
  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            {finalColors.map((color, index) => (
              <stop
                key={index}
                offset={`${(index / (finalColors.length - 1)) * 100}%`}
                stopColor={color}
              />
            ))}
          </linearGradient>
        </defs>
      </svg>
      <Box
        component="span"
        sx={{
          display: 'contents',
          '& svg path': {
            fill: `url(#${gradientId})`,
            stroke: `url(#${gradientId})`,
          }
        }}
      >
        <BrainIcon />
      </Box>
    </>
  );
};

// Variantes de animación tipo macOS
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Definimos un filtro SVG para el inner shadow
const SVG_FILTER_ID = 'innerShadowFilter';

// Estilos específicos para AppLayout
const AppIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

const AppIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gradient' && prop !== 'gradientDark' && prop !== 'appId',
})(({ theme, selected, disabled, gradient, gradientDark, appId }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';

  // Selecciona el gradiente apropiado según el modo
  const baseGradient = gradient
    ? (isDarkMode && gradientDark ? gradientDark : gradient)
    : (isDarkMode
      ? 'linear-gradient(to bottom, #1C1C1E, #0F0F11)'
      : 'linear-gradient(to bottom, #1BB3F3, #186CE6)');

  // Funciones auxiliares para AppIcon
  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  const invertColor = (hex) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');
    return `#${invR}${invG}${invB}`;
  };

  const isChatInDark = appId === 'chat' && isDarkMode && !isWallpaperMode;
  const chatLightColor = isChatInDark ? extractColors(gradientDark || gradient)[0] || '#415B7E' : null;

  // Para Fin en ambos modos, invertir los colores del gradiente (negativo) para diferenciarse
  const isFin = appId === 'fin' && !isWallpaperMode;
  let borderGradient = baseGradient;

  if (isFin) {
    const gradColors = extractColors(gradientDark || gradient);
    const invertedColors = gradColors.map(invertColor);
    borderGradient = `linear-gradient(to bottom, ${invertedColors.join(', ')})`;
  }

  return {
    position: 'relative',
    width: '60px',
    height: '60px',
    borderRadius: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    // En wallpaper: fondo semi-transparente con blur
    // En dark/light: fondo sólido
    background: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.15)'
      : (isDarkMode ? '#1C1C1E' : '#FFFFFF'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
    boxShadow: selected
      ? `0 0 0 2px ${theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'}, 0 4px 8px rgba(0,0,0,0.2)`
      : isWallpaperMode
        ? '0 2px 8px rgba(0, 0, 0, 0.1)'
        : '0 2px 5px rgba(0,0,0,0.1)',
    opacity: disabled ? 0.5 : 1,
    transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
    cursor: 'pointer',
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    WebkitFontSmoothing: 'antialiased',
    // Pseudo-elemento para el borde con gradiente (solo en light/dark)
    // Chat en dark usa borde sólido del color más claro
    // Fin en dark usa gradiente invertido
    '&::before': isWallpaperMode ? {} : {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '15px',
      padding: '2px',
      background: isChatInDark ? chatLightColor : borderGradient,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
    '& svg': {
      width: '70%',
      height: '70%',
      pointerEvents: 'none',
      display: 'block',
    },
  };
});

const AppLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  textAlign: 'center',
  maxWidth: '80px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const AppDock = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? 'rgba(28, 28, 30, 0.8)' : 'rgba(242, 242, 247, 0.8)'),
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '20px',
    marginTop: '20px',
    margin: '20px auto 0',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : '1px solid transparent',
  };
});

const AppLayout = ({
  state,
  isWaiting,
  inputRef,
  handleMessageSubmit,
  updateState,
  debouncedUpdateSuggestions,
  waitingMessage,
  handleCardClick,
  isPro,
  selectedCard,
  handleCustomizeClick,
  interfaceStyle,
  selectedApps = ['chat'],
  t
}) => {
  const theme = useTheme();
  // Si no recibimos t como prop, usamos el hook
  const translation = useTranslation();
  const { t: tFunc } = translation;
  const translate = t || tFunc;

  // Mapeo completo de apps disponibles
  // Todas las apps usan cerebro con gradientes específicos
  const availableAppsMap = useMemo(() => ({
    chat: {
      text: translate('homeInput.cards.chat'),
      action: "chat",
      isProOnly: false,
      gradient: CHAT_GRADIENT_LIGHT,
      gradientDark: CHAT_GRADIENT_DARK
    },
    fin: {
      text: 'Fin',
      action: "fin",
      isProOnly: false,
      gradient: JOB_GRADIENT_LIGHT,
      gradientDark: JOB_GRADIENT_DARK
    },
    fit: {
      text: 'Fit',
      action: "fit",
      isProOnly: false,
      gradient: FIT_GRADIENT_LIGHT,
      gradientDark: FIT_GRADIENT_DARK
    },
    health: {
      text: 'Health',
      action: "health",
      isProOnly: false,
      gradient: HEALTH_GRADIENT_LIGHT,
      gradientDark: HEALTH_GRADIENT_DARK
    },
    ups: {
      text: translate('homeInput.cards.ups'),
      action: "ups",
      isProOnly: false,
      gradient: UPS_GRADIENT_LIGHT,
      gradientDark: UPS_GRADIENT_DARK
    }
  }), [translate]);

  // Filtrar apps basadas en selectedApps del usuario
  const appIcons = useMemo(() => {
    return selectedApps
      .filter(appId => availableAppsMap[appId]) // Solo apps válidas
      .map(appId => availableAppsMap[appId]);
  }, [selectedApps, availableAppsMap]);

  // Función para manejar clics en los iconos de la app
  const handleAppIconClick = (app) => {
    // Para cualquier app, redirigir directamente a la sección correspondiente
    const targetSection = app.action;

    if (targetSection) {
      // Disparar evento personalizado para navegar directamente
      const navigateEvent = new CustomEvent('navigateToSection', {
        detail: { section: targetSection }
      });
      window.dispatchEvent(navigateEvent);

      // También actualizar localStorage
      localStorage.setItem('activeSection', targetSection);

      // Activar la visibilidad en navegación si es necesario
      if (targetSection === 'ups') {
        localStorage.setItem('showUpsInNav', 'true');
      } else if (targetSection === 'health') {
        localStorage.setItem('showHealthInNav', 'true');
      } else if (targetSection === 'fit') {
        localStorage.setItem('showFitInNav', 'true');
      }
    }
  };

  return (
    <ContentWrapper
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        width: '100%',
        maxWidth: '1200px'
      }}
    >
      {/* Definimos el filtro SVG para inner shadow */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <filter id={SVG_FILTER_ID}>
            <feOffset dx="0" dy="1" />
            <feGaussianBlur stdDeviation="1" result="offset-blur" />
            <feComposite
              operator="out"
              in="SourceGraphic"
              in2="offset-blur"
              result="inverse"
            />
            <feFlood floodColor="black" floodOpacity="0.4" result="color" />
            <feComposite operator="in" in="color" in2="inverse" result="shadow" />
            <feComposite operator="over" in="shadow" in2="SourceGraphic" />
          </filter>
        </defs>
      </svg>

      <TypedText
        component={motion.h2}
        variants={itemVariants}
        variant="h2"
        gutterBottom
        sx={{
          textAlign: 'center !important',
          width: '100%',
          margin: '0 auto',
          marginBottom: '15px'
        }}
      >
        {translate('homeInput.heading')}
      </TypedText>

      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}
      >
        <StyledForm onSubmit={handleMessageSubmit}>
          <InputField
            state={state}
            isWaiting={isWaiting}
            inputRef={inputRef}
            onSubmit={handleMessageSubmit}
            onStateUpdate={updateState}
            onSuggestionsUpdate={debouncedUpdateSuggestions}
            interfaceStyle={interfaceStyle}
            onSuggestionClick={handleCardClick}
          />
        </StyledForm>
      </Box>

      <AnimatePresence mode="wait">
        {isWaiting && (
          <motion.div
            key="waiting"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <WaitingMessage>
              {waitingMessage}...
            </WaitingMessage>
          </motion.div>
        )}
      </AnimatePresence>

      {!isWaiting && (
        <Box component={motion.div} variants={itemVariants}>
          <AppDock>
          <Grid container spacing={3} justifyContent="center">
            {appIcons.map((app, index) => {
              const isDisabled = app.isProOnly && !isPro;
              return (
                <Grid item key={`${app.text}-${index}`}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.08,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    whileHover={!isDisabled ? { scale: 1.08, y: -4 } : {}}
                    whileTap={!isDisabled ? { scale: 0.95 } : {}}
                  >
                    <AppIconContainer
                      onClick={() => !isDisabled && handleAppIconClick(app)}
                      sx={{ opacity: isDisabled ? 0.5 : 1 }}
                    >
                      <AppIcon
                        selected={selectedCard === app.text}
                        disabled={isDisabled}
                        gradient={app.gradient}
                        gradientDark={app.gradientDark}
                        appId={app.action}
                      >
                        <BrainIconWithGradient
                          gradient={app.gradient}
                          gradientDark={app.gradientDark}
                          appId={app.action}
                        />
                      </AppIcon>
                      <AppLabel>{app.text}</AppLabel>
                    </AppIconContainer>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
          
          <Box display="flex" justifyContent="center" width="100%" mt={4}>
            <CustomizeButton onClick={handleCustomizeClick} />
          </Box>
          </AppDock>
        </Box>
      )}
    </ContentWrapper>
  );
};

AppLayout.propTypes = {
  state: PropTypes.object.isRequired,
  isWaiting: PropTypes.bool.isRequired,
  inputRef: PropTypes.object.isRequired,
  handleMessageSubmit: PropTypes.func.isRequired,
  updateState: PropTypes.func.isRequired,
  debouncedUpdateSuggestions: PropTypes.func.isRequired,
  waitingMessage: PropTypes.string.isRequired,
  handleCardClick: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  selectedCard: PropTypes.string,
  handleCustomizeClick: PropTypes.func.isRequired,
  interfaceStyle: PropTypes.string.isRequired,
  selectedApps: PropTypes.arrayOf(PropTypes.string),
  t: PropTypes.func,
  onSuggestionClick: PropTypes.func
};

export default AppLayout;